/**
 * The single press's receipt — **both halves, per part, always** (um-app/06 §4.5, §5.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE RULE THIS COMPONENT EXISTS TO KEEP
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * > A press that sent one of two things must never render as `Sent`.
 *
 * The type makes the wrong render unconstructible — `FinalPushBody` cannot be read without
 * discriminating `outcome`, and each arm names its halves concretely — but a type only stops
 * the code from *claiming* the wrong thing. This component is what stops the SCREEN from
 * implying it: the heading names the outcome in words, and **both halves are drawn on every
 * outcome**, each with its own state, its own verbatim detail and its own receipt id. There
 * is no rendering path on which a failed half is merely absent, because an absent half reads
 * as a half nobody needs to think about.
 *
 * Two levels of honesty, kept apart because they are different questions:
 *   1. did the press reach our server, or is it queued on the phone? — that is the caller's
 *      `WriteOutcome`, rendered by the composer, not here;
 *   2. what did TMS do with each half? — that is this component, and it never speaks about 1.
 *
 * `replayed` is shown when true. A press that drained from the queue and was answered from
 * stored receipts sent nothing this time, and a receipt that did not say so would suggest a
 * second delivery to TMS that never happened.
 */

import type {
  FinalPushBody,
  FinalPushFailedPart,
  FinalPushSentPart,
} from "../../api/types.js";

/** What each half is called on screen. Field remarks capitalised, per the house rule. */
const PART_LABEL = { marks: "Category Marks", report: "Final Report" } as const;

/**
 * What each failure reason means for the reader's next act — the same five the gateway can
 * return. Said in words rather than left as a machine token, because the TD's and the UM's
 * next step differs for every one of them.
 */
const REASON_NOTE: Readonly<Record<string, string>> = {
  transport_unavailable:
    "The TMS write transport is not built yet — it is proved against real TMS in spike week. " +
    "Use Copy For TMS and paste it in yourself.",
  session_expired: "Your TMS sign-in has expired. Sign in to TMS, then press again.",
  unreachable: "TMS could not be reached. Nothing was sent; press again when it is back.",
  form_changed:
    "The TMS form is not the shape this build expects, so nothing was written. This needs " +
    "looking at before pressing again.",
  rejected_by_tms: "TMS refused it. The reason it gave is printed above, verbatim.",
};

function PartRow({
  which,
  part,
}: {
  which: "marks" | "report";
  part: FinalPushSentPart | FinalPushFailedPart;
}): React.JSX.Element {
  return (
    <li
      className={`finalpart finalpart--${part.sent ? "sent" : "failed"}`}
      data-testid={`final-part-${which}`}
    >
      <span className="finalpart__nm">{PART_LABEL[which]}</span>
      <span
        className={`finalpart__state ${part.sent ? "finalpart__state--sent" : "finalpart__state--failed"}`}
        data-testid={`final-part-state-${which}`}
      >
        {part.sent ? "In TMS As Draft" : "Not Sent"}
      </span>
      <span className="finalpart__detail">
        {part.sent ? (
          <>
            {part.receipt.detail}
            {part.receipt.tmsReference === null ? null : (
              <>
                {" "}
                TMS reference <span className="num">{part.receipt.tmsReference}</span>.
              </>
            )}
          </>
        ) : (
          <>
            {/* The gateway's own words, never paraphrased. */}
            <b>{part.detail}</b>{" "}
            {REASON_NOTE[part.reason] ?? "The reason TMS gave is printed above, verbatim."}
          </>
        )}
      </span>
      <span className="finalpart__id num">Receipt {part.receipt.id.slice(0, 8)}</span>
    </li>
  );
}

/** The heading, and the sentence under it. One per outcome, and none of them is ambiguous. */
function headingFor(outcome: FinalPushBody["outcome"]): { title: string; note: string } {
  if (outcome === "sent") {
    return {
      title: "Pushed As Draft — Both Parts",
      note:
        "The marks and the report are both in TMS as drafts. Publishing stays a deliberate " +
        "step you take inside TMS; nothing here makes the grading final.",
    };
  }
  if (outcome === "partial") {
    return {
      title: "Partly Pushed — One Part Did Not Send",
      note:
        "One half of this press reached TMS and the other did not. Both are recorded below " +
        "with what happened to each. Nothing is retried automatically — pressing again is a " +
        "new press, and it will send the part that is still outstanding.",
    };
  }
  return {
    title: "Not Pushed — Neither Part Sent",
    note:
      "Nothing reached TMS. Both attempts are recorded below with the reason. Your marks and " +
      "your report are untouched and still here; use Copy For TMS if you need them now.",
  };
}

export function FinalPushReceipt({ result }: { result: FinalPushBody }): React.JSX.Element {
  const { title, note } = headingFor(result.outcome);

  return (
    <section
      className={`finalreceipt finalreceipt--${result.outcome}`}
      role="status"
      aria-label={title}
      data-testid="final-push-receipt"
    >
      <b className="finalreceipt__title" data-testid="final-push-outcome">
        {title}
      </b>
      <p className="finalreceipt__note">{note}</p>

      {/* BOTH halves, on every outcome. An absent half reads as one nobody need think about. */}
      <ul className="finalparts">
        <PartRow which="marks" part={result.marks} />
        <PartRow which="report" part={result.report} />
      </ul>

      <p className="finalreceipt__foot">
        One press ·{" "}
        <span className="num" data-testid="final-push-pressid">
          {result.pressId.slice(0, 8)}
        </span>{" "}
        · both parts recorded under it.
        {result.replayed ? (
          <>
            {" "}
            <b data-testid="final-push-replayed">
              This press had already been delivered — nothing was sent again.
            </b>{" "}
            What you see is the record of the original press.
          </>
        ) : null}
      </p>
    </section>
  );
}
